import type { ReactNode } from 'react';
import { MapPin, Phone, User as UserIcon, Landmark, FileBadge, IndianRupee, Boxes, Info } from 'lucide-react';
import { SECTION_TINT, Tooltip, cn } from '../../ui';
import { RoleBadge } from '../../components/RoleBadge';
import { PersonLink, RetailerLink, ProductLink } from '../../components/EntityLinks';
import { stateName } from '../../constants/indianStates';
import { humanLabel } from './activityConstants';

/**
 * How an audit payload is READ, rather than how it happens to be stored.
 *
 * A create entry arrives as whatever the endpoint was posted — for a dealer that is eleven keys in
 * insertion order, so the dialog opened with "City, Mobile, Pincode, Latitude, Longitude, State
 * Code, Company Name, …": the one fact that identifies the record sat seventh, the two halves of a
 * map pin were separate rows, and a beat was a bare `126`. The owner's verdict was that the
 * information is scattered (2026-09-29).
 *
 * So the fields are grouped by MEANING and ordered, using the same section vocabulary and tints as
 * the retailer, farmer and staff detail views (`SECTION_TINT` is keyed by meaning precisely so the
 * same kind of thing is the same colour on every screen). Anything unrecognised still appears, in a
 * final "Other details" group — an audit trail may never quietly drop a field.
 */

export type FieldEntry = [string, unknown];
export interface FieldGroup { key: string; title: string; icon: ReactNode; tint: string; entries: FieldEntry[] }

/** Normalised key for matching: `assigned_sales_person_id` and `assignedSalesPersonId` both → `assignedsalespersonid`. */
const norm = (k: string) => k.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Ordered: the first pattern a field matches wins, so put the specific ones first. */
const GROUPS: Array<{ key: string; title: string; icon: ReactNode; tint: string; match: RegExp }> = [
  { key: 'identity', title: 'Identity', icon: <UserIcon className="size-3.5" />, tint: SECTION_TINT.identity,
    match: /^(companyname|fullname|name|firstname|lastname|displayname|code|retailercode|sku|title|gender|dob|dateofbirth|language|type|category|subcategory|status|isactive|active|verificationstatus|kycstatus|source|registeredvia)$/ },
  { key: 'contact', title: 'Contact', icon: <Phone className="size-3.5" />, tint: SECTION_TINT.contact,
    match: /(mobile|phone|whatsapp|email|contactperson)/ },
  { key: 'place', title: 'Where they are', icon: <MapPin className="size-3.5" />, tint: SECTION_TINT.place,
    match: /(address|city|village|district|taluka|state|pincode|zip|latitude|longitude|gps|coordinates|landmark|route|beat|territory)/ },
  { key: 'documents', title: 'Documents & compliance', icon: <FileBadge className="size-3.5" />, tint: SECTION_TINT.documents,
    match: /(gst|pan|aadhaar|aadhar|licence|license|kyc|document|photo|attachment|proof|certificate)/ },
  { key: 'money', title: 'Commercial terms', icon: <IndianRupee className="size-3.5" />, tint: SECTION_TINT.money,
    match: /(credit|discount|price|rate|amount|total|tax|scheme|payment|balance|wallet|advance|commission)/ },
  { key: 'items', title: 'Items & quantities', icon: <Boxes className="size-3.5" />, tint: SECTION_TINT.farm,
    match: /(product|variant|batch|item|line|qty|quantity|uom|stock|warehouse|land|acre|crop)/ },
  { key: 'assignment', title: 'Who covers them', icon: <Landmark className="size-3.5" />, tint: SECTION_TINT.assignment,
    match: /(assigned|owner|salesperson|reportsto|manager|approver|team|shift|session)/ },
];
const OTHER = { key: 'other', title: 'Other details', icon: <Info className="size-3.5" />, tint: SECTION_TINT.system };

/** Which group a field belongs to — `territory` before `assigned…` is why order matters above. */
function groupKeyFor(field: string): string {
  const n = norm(field);
  return GROUPS.find((g) => g.match.test(n))?.key ?? OTHER.key;
}

/** A field's rank inside its own group, so the identity of the record leads rather than trails. */
const LEAD = ['companyname', 'fullname', 'name', 'code', 'contactperson', 'mobile', 'addressline1', 'address', 'city', 'territoryid', 'territory'];
const leadRank = (field: string) => { const i = LEAD.indexOf(norm(field)); return i < 0 ? LEAD.length : i; };

/**
 * Group + order payload entries for display, merging the pieces that are one fact:
 * latitude and longitude are a single map pin, not two numbers on two rows.
 */
export function groupPayloadFields(entries: FieldEntry[]): FieldGroup[] {
  const lat = entries.find(([k]) => norm(k) === 'latitude');
  const lng = entries.find(([k]) => norm(k) === 'longitude');
  const merged: FieldEntry[] = entries.filter(([k]) => !['latitude', 'longitude'].includes(norm(k)));
  if (lat && lng) merged.push(['gpsCoordinates', `${lat[1]}, ${lng[1]}`]);
  else if (lat || lng) merged.push((lat ?? lng)!);

  const byKey = new Map<string, FieldEntry[]>();
  for (const e of merged) {
    const g = groupKeyFor(e[0]);
    if (!byKey.has(g)) byKey.set(g, []);
    byKey.get(g)!.push(e);
  }
  const order = [...GROUPS, OTHER];
  return order
    .filter((g) => byKey.get(g.key)?.length)
    .map((g) => ({
      key: g.key, title: g.title, icon: g.icon, tint: g.tint,
      entries: byKey.get(g.key)!.sort((a, b) => leadRank(a[0]) - leadRank(b[0])),
    }));
}

/** Rank a field by its group + position, so a before→after table lists related rows together too. */
export function fieldSortRank(field: string): number {
  const gi = [...GROUPS, OTHER].findIndex((g) => g.key === groupKeyFor(field));
  return gi * 100 + leadRank(field);
}

/**
 * Sentence case, and never a trailing "ID" — the value beside it is a NAME now, so "Assigned sales
 * person", not "Assigned Sales Person ID". Acronyms (GST, PAN, KYC) keep their capitals.
 */
/** Labels the generic camel-case rule cannot get right, or that the VALUE has outgrown. */
const LABEL_OVERRIDE: Record<string, string> = {
  statecode: 'State',            // the value is the state's name now, not its code
  territoryid: 'Territory',
  gpscoordinates: 'GPS coordinates',
  addressline1: 'Address line 1',
  addressline2: 'Address line 2',
  whatsappnumber: 'WhatsApp',
};

export function fieldLabel(key: string): string {
  const over = LABEL_OVERRIDE[norm(key)];
  if (over) return over;
  const words = humanLabel(key).replace(/\s+ID$/i, '').split(' ');
  return words
    .map((w, i) => (w === w.toUpperCase() && w.length > 1 ? w : i === 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w.toLowerCase()))
    .join(' ');
}

/** Fields whose value is one of a fixed set of words — safe to print in proper case. */
const ENUM_FIELD = /(status|type|source|via|level|mode|channel|kind|reason|outcome|stage|priority|gender|frequency|unit)$/;

/** A value that gets copied or dialled earns a chip, the way the detail pages show the same fact. */
/**
 * THE SCRUBBED-VALUE MARKER, AND WHY A PINCODE CARRIES ONE.
 *
 * Values are redacted on write so a secret never lands in `activity_logs`. The rule keys on the FIELD
 * NAME, and `pin` used to swallow a postal PIN code — so every dealer created before that was fixed has
 * `pincode: "[REDACTED]"` on file (67 rows on dev; the newest 28 Sep). The value is gone: it was never
 * written, so nothing can recover it, and back-filling one from the dealer's CURRENT pincode would be
 * inventing audit history.
 *
 * What we can fix is the reading — in two steps. For a field that is not secret AND whose record is
 * still there, the server passes through what the RECORD says today (`{ __fromRecord }`), because
 * "Not recorded" beside a dealer page showing PIN code 422605 is a worse answer than the number with
 * its source named (owner, 2026-09-29). Only when even that is unavailable does the field say it was
 * not recorded; a genuinely secret one says it is never stored.
 */
export const REDACTED_MARKER = '[REDACTED]';
/** Today's rule, mirroring `backend/src/common/utils/redact.ts` — `pin` here means a secret PIN. */
const SECRET_FIELD = /pass|token|otp|secret|cvv|pin(?!_?code)|auth|key|hash|credential/i;

export function redactedNote(field: string | null, value: unknown): ReactNode | null {
  if (String(value ?? '').trim() !== REDACTED_MARKER) return null;
  const secret = field == null || SECRET_FIELD.test(field);
  return (
    <Tooltip label={secret
      ? 'Secret values are never written to the audit log.'
      : 'An older version of the log removed this before saving. New entries record it.'}>
      <span className="inline-flex items-center gap-1 rounded-control bg-surface-2 px-2 py-0.5 type-body italic text-muted">
        <Info className="size-3" />{secret ? 'Never stored' : 'Not recorded'}
      </span>
    </Tooltip>
  );
}

/**
 * A value the entry itself lost, carried over from the record it points at.
 *
 * It renders as the plain value, exactly like a logged one. A visible "from the record" caption rode
 * beside it for one round and was cut (owner, 2026-09-29: "do we really need to show this to the
 * user, if not remove it") — where the value came from is a developer's footnote about a bug we have
 * already fixed, and a dealer's PIN code does not change by being read a day later. The provenance
 * survives on hover for anyone actually auditing the entry.
 */
export interface FromRecord { __fromRecord: string }
export const isFromRecord = (v: unknown): v is FromRecord =>
  !!v && typeof v === 'object' && typeof (v as FromRecord).__fromRecord === 'string';

export function FromRecordValue({ field, value }: { field?: string | null; value: FromRecord }) {
  const shown = chipFor(field ?? '', value.__fromRecord) ?? <span className="type-body-strong text-content">{value.__fromRecord}</span>;
  return (
    <Tooltip label="This entry did not keep the value (an old bug removed it). This is what the record says today.">
      <span className="inline-flex items-center">{shown}</span>
    </Tooltip>
  );
}

export function chipFor(field: string, value: unknown): ReactNode | null {
  const n = norm(field);
  const s = String(value ?? '').trim();
  if (!s) return null;
  // Before any chip: a scrubbed value is not a value, whatever field it sits on.
  const scrubbed = redactedNote(field, s);
  if (scrubbed) return scrubbed;
  // A stored enum is a word, not a shout: "Registered via SALES" reads as an error message. Only
  // fields that HOLD an enum are softened — a PAN, a GSTIN or an SKU is also all-caps, and
  // "ABCDE1234F" must never come out as "Abcde1234f".
  if (ENUM_FIELD.test(n) && /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/.test(s)) {
    return <span className="type-body-strong text-content">{s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}</span>;
  }
  if (/^(mobile|phone|whatsappnumber|whatsapp|alternatemobile|contactnumber)$/.test(n)) {
    return (
      <a href={`tel:${s}`} className="inline-flex items-center gap-1 rounded-control bg-emerald-500/10 px-2 py-0.5 font-mono type-body-strong text-emerald-700 hover:underline dark:text-emerald-400">
        <Phone className="size-3" />{s}
      </a>
    );
  }
  if (/^email$/.test(n)) {
    return <a href={`mailto:${s}`} className="break-all type-body-strong text-brand-700 hover:underline dark:text-brand-400">{s}</a>;
  }
  if (/^(pincode|zipcode|gpscoordinates|latitude|longitude)$/.test(n)) {
    return <span className="inline-block rounded-control bg-sky-500/10 px-2 py-0.5 font-mono type-body-strong text-sky-700 dark:text-sky-400">{s}</span>;
  }
  // "MH" / "27" is not a place a reader recognises — the state is named, with the code kept on hover
  // for anyone reconciling a GSTIN.
  if (/^(statecode|state)$/.test(n)) {
    const named = stateName(s);
    return named === s
      ? <span className="type-body-strong text-content">{s}</span>
      : <span className="type-body-strong text-content" title={`State code ${s}`}>{named}</span>;
  }
  if (/^(territoryid|territory|parentterritoryid|beat)$/.test(n)) {
    return (
      <span className="inline-flex items-center gap-1 rounded-control bg-violet-500/10 px-2 py-0.5 type-body-strong text-violet-700 dark:text-violet-400">
        <MapPin className="size-3" />{s}
      </span>
    );
  }
  return null;
}

/**
 * A resolved reference the server sent instead of a bare id: `{ __ref, id, name, role? }`.
 * It carries enough to OPEN the thing and to badge who they are.
 */
export interface PayloadRef { __ref: string; id: string; name: string; role?: string | null }
export const isRef = (v: unknown): v is PayloadRef =>
  !!v && typeof v === 'object' && typeof (v as PayloadRef).__ref === 'string' && typeof (v as PayloadRef).name === 'string';

/**
 * A person, dealer or product in a payload, as the rest of the app shows them: the name opens the
 * page about them, and a person wears their role badge — "Assigned sales person: Asmita Kale" was a
 * dead end that said nothing about who she is (owner, 2026-09-29).
 *
 * Every link component here degrades to plain text when the viewer cannot open the target, so this
 * never produces a link that would 403.
 */
export function RefValue({ value }: { value: PayloadRef }) {
  const name = <span className="type-body-strong">{value.name}</span>;
  if (value.__ref === 'user') {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <PersonLink id={value.id} role={value.role}>{name}</PersonLink>
        {value.role && <RoleBadge role={value.role} />}
      </span>
    );
  }
  if (value.__ref === 'retailer') return <RetailerLink id={value.id}>{name}</RetailerLink>;
  if (value.__ref === 'product') return <ProductLink name={value.name}>{name}</ProductLink>;
  if (value.__ref === 'territory') {
    return (
      <span className="inline-flex items-center gap-1 rounded-control bg-violet-500/10 px-2 py-0.5 type-body-strong text-violet-700 dark:text-violet-400">
        <MapPin className="size-3" />{value.name}
      </span>
    );
  }
  return name;
}

/** Long prose (an address, a note) needs the full width or it wraps into a two-word column. */
export const isWide = (value: unknown): boolean =>
  (typeof value === 'string' && value.length > 44) || (value !== null && typeof value === 'object' && !isRef(value) && !isFromRecord(value));

export function FieldGroupCard({ group, children, className }: { group: FieldGroup; children: ReactNode; className?: string }) {
  return (
    <section className={cn('rounded-card border border-line bg-surface p-3.5', className)}>
      <h4 className="mb-2.5 flex items-center gap-2">
        <span className={cn('flex size-6 shrink-0 items-center justify-center rounded-md', group.tint)}>{group.icon}</span>
        <span className="type-overline text-content">{group.title}</span>
        <span className="ml-1 h-px flex-1 bg-line/70" aria-hidden />
      </h4>
      {children}
    </section>
  );
}
